import React, { useState, useEffect, useRef } from 'react';
import { X, User, Mail, Lock, Palette, Upload, Camera, Sun, Moon, Monitor, Save, AlertCircle, CheckCircle, Zap, Compass as Compress, Crown, Info } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import { Theme } from '../types';
import { compressImage, formatFileSize, validateImageFile, CompressionResult } from '../utils/imageCompression';
import SubscriptionManager from './SubscriptionManager';
import BoltBadge from './BoltBadge';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, updateProfile, changePassword, resendConfirmationEmail } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const { currentPlan, planLimits } = useSubscriptionLimits();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'subscription' | 'preferences'>('profile');
  
  // Profile form state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [imageError, setImageError] = useState(false);

  // Reset form when user changes or modal opens
  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setAvatar(user.avatar || '');
      setImageError(false);
    }
  }, [user, isOpen]);

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setMessage({ type: 'error', text: validation.error || 'Invalid file' });
      return;
    }

    setCompressing(true);
    setMessage(null);

    try {
      // Compress the image
      const result = await compressImage(file, {
        maxWidth: 200,
        maxHeight: 200,
        quality: 0.8,
        maxSizeKB: 50,
        format: 'jpeg'
      });

      setAvatar(result.compressedDataUrl);
      setImageError(false);

    } catch (error) {
      console.error('Image compression failed:', error);
      setMessage({ type: 'error', text: 'Failed to process image. Please try a different file.' });
    } finally {
      setCompressing(false);
    }
  };

  const generateAvatar = () => {
    const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6366F1'];
    const color = colors[Math.floor(Math.random() * colors.length)];
    const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    
    const svg = `
      <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="100" r="100" fill="${color}"/>
        <text x="100" y="125" font-family="Arial, sans-serif" font-size="60" font-weight="bold" fill="white" text-anchor="middle">${initials}</text>
      </svg>
    `;
    
    const dataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
    setAvatar(dataUrl);
    setImageError(false);
  };

  const handleSaveProfile = async () => {
    if (!user) throw new Error('No user logged in');

    setLoading(true);
    setMessage(null);
    
    try {
      await updateProfile({
        name: name.trim(),
        email: email.trim(),
        avatar: avatar || undefined
      });
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    setLoading(true);
    setMessage(null);
    
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage({ type: 'success', text: 'Password changed successfully!' });
    } catch (error: any) {
      console.error('Failed to change password:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!user) return;
    
    setLoading(true);
    setMessage(null);
    
    try {
      await resendConfirmationEmail(user.email);
      setMessage({ type: 'success', text: 'Confirmation email sent! Please check your inbox.' });
    } catch (error: any) {
      console.error('Failed to resend confirmation email:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to resend confirmation email' });
    } finally {
      setLoading(false);
    }
  };

  const getThemeIcon = (themeType: Theme) => {
    switch (themeType) {
      case 'light':
        return <Sun className="w-4 h-4" />;
      case 'dark':
        return <Moon className="w-4 h-4" />;
      case 'system':
        return <Monitor className="w-4 h-4" />;
    }
  };

  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'Free Plan';
      case 'pro':
        return 'Pro Plan';
      case 'enterprise':
        return 'Enterprise Plan';
      default:
        return 'Free Plan';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'from-slate-500 to-slate-600';
      case 'pro':
        return 'from-blue-500 to-purple-600';
      case 'enterprise':
        return 'from-purple-600 to-indigo-700';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  const getInitials = (name: string): string => {
    if (!name || typeof name !== 'string') return 'U';
    return name.split(' ')
               .map(n => n.charAt(0))
               .join('')
               .toUpperCase()
               .slice(0, 2) || 'U';
  };

  const getAvatarColor = (name: string): string => {
    const colors = [
      'bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-yellow-500', 
      'bg-red-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500'
    ];
    
    if (!name || typeof name !== 'string') return colors[0];
    
    const charCode = name.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Portal-like overlay that covers the entire viewport */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999
        }}
        onClick={handleBackdropClick}
      >
        {/* Centered modal container */}
        <div className="min-h-screen flex items-center justify-center p-4">
          <div 
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-200">User Profile</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>

            {/* Message Display */}
            {message && (
              <div className={`mx-4 sm:mx-6 mt-4 p-3 rounded-lg flex items-center gap-3 ${
                message.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span className="text-sm font-medium">{message.text}</span>
              </div>
            )}

            <div className="border-b border-slate-200 dark:border-slate-700">
              <div className="flex overflow-x-auto scrollbar-hide">
                {(['profile', 'security', 'subscription', 'preferences'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-shrink-0 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-900/20'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 sm:p-6 max-h-[60vh] overflow-y-auto">
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                        {avatar && !imageError ? (
                          <img 
                            src={avatar} 
                            alt="Avatar" 
                            className="w-full h-full object-cover"
                            onError={handleImageError}
                            onLoad={handleImageLoad}
                          />
                        ) : (
                          <div className={`w-full h-full ${getAvatarColor(user?.name || '')} flex items-center justify-center text-white text-lg font-bold`}>
                            {getInitials(user?.name || '')}
                          </div>
                        )}
                      </div>
                      <label className="absolute bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-600 transition-colors">
                        {compressing ? (
                          <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent"></div>
                        ) : (
                          <Camera className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                          disabled={compressing}
                        />
                      </label>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{user?.name}</h3>
                      <p className="text-slate-600 dark:text-slate-400">{user?.email}</p>
                      
                      {/* Email verification status */}
                      <div className={`mt-2 flex items-center gap-2 text-sm ${
                        user?.emailConfirmed 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-orange-600 dark:text-orange-400'
                      }`}>
                        {user?.emailConfirmed ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span>Email verified</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4" />
                            <span>Email not verified</span>
                            <button
                              onClick={handleResendConfirmation}
                              className="text-blue-600 dark:text-blue-400 hover:underline ml-2"
                              disabled={loading}
                            >
                              Resend verification
                            </button>
                          </>
                        )}
                      </div>
                      
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={generateAvatar}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors flex items-center gap-1"
                        >
                          <Zap className="w-3 h-3" />
                          Generate Avatar
                        </button>
                        {compressing && (
                          <span className="text-sm text-orange-600 dark:text-orange-400 flex items-center gap-1">
                            <Compress className="w-3 h-3" />
                            Compressing...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        disabled={loading}
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Changing your email will require re-verification
                      </p>
                    </div>

                    <button
                      onClick={handleSaveProfile}
                      disabled={loading || !name.trim() || !email.trim()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {loading ? 'Saving...' : 'Save Profile'}
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                      <Lock className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Change Password</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        disabled={loading}
                      />
                    </div>

                    <button
                      onClick={handleChangePassword}
                      disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <Lock className="w-4 h-4" />
                      )}
                      {loading ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>

                  {/* Email verification section */}
                  <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-1">Email Verification</h4>
                        <p className="text-sm text-blue-600 dark:text-blue-400 mb-3">
                          {user?.emailConfirmed 
                            ? 'Your email has been verified. You have full access to all features.'
                            : 'Please verify your email address to ensure account security and access all features.'}
                        </p>
                        {!user?.emailConfirmed && (
                          <button
                            onClick={handleResendConfirmation}
                            disabled={loading}
                            className="text-sm px-3 py-1.5 bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                          >
                            Resend Verification Email
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'subscription' && (
                <SubscriptionManager />
              )}

              {activeTab === 'preferences' && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <Palette className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Appearance</h3>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                      Theme
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {(['light', 'dark', 'system'] as Theme[]).map((themeOption) => (
                        <button
                          key={themeOption}
                          onClick={() => setTheme(themeOption)}
                          className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 ${
                            theme === themeOption
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 shadow-lg transform scale-105'
                              : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                          }`}
                        >
                          {getThemeIcon(themeOption)}
                          <span className="font-medium capitalize">{themeOption}</span>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                      System theme automatically switches between light and dark based on your device settings
                    </p>
                  </div>

                  {/* Theme Preview */}
                  <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-xl border border-slate-200 dark:border-slate-600">
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Theme Preview</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded"></div>
                        <span className="text-sm text-slate-600 dark:text-slate-400">Primary Color</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-slate-300 dark:bg-slate-600 rounded"></div>
                        <span className="text-sm text-slate-600 dark:text-slate-400">Background Color</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-slate-800 dark:bg-slate-200 rounded"></div>
                        <span className="text-sm text-slate-600 dark:text-slate-400">Text Color</span>
                      </div>
                    </div>
                  </div>

                  {/* Built with Bolt.new Badge */}
                  <div className="flex justify-center mt-6">
                    <BoltBadge />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfileModal;